import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../features/auth/AuthContext';
import { useJoinInfo, useJoinLeague } from '../../features/leagues/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/Button';
import { ApiError } from '../../lib/api';

/**
 * Pagina di invito (/entra/:codice): mostra la lega e permette di entrare.
 * Funziona anche senza login: in quel caso porta prima ad accedere/registrarsi
 * conservando la destinazione.
 */
export function JoinPage() {
  const { code = '' } = useParams();
  usePageTitle('Invito a una lega');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { announce } = useAnnouncer();
  const info = useJoinInfo(code);
  const join = useJoinLeague();
  const [error, setError] = useState<string | null>(null);

  if (info.isLoading) {
    return (
      <AuthLayout title="Invito a una lega">
        <p role="status">Verifica dell’invito in corso…</p>
      </AuthLayout>
    );
  }

  if (info.isError || !info.data) {
    return (
      <AuthLayout title="Invito non valido">
        <p role="alert" className="font-medium text-danger">
          Questo invito non esiste o non è più valido. Chiedi un nuovo link a chi ti ha invitato.
        </p>
      </AuthLayout>
    );
  }

  const { league, valid, requiresApproval } = info.data;

  return (
    <AuthLayout title="Sei stato invitato!">
      <div className="rounded-2xl border border-ink-200 p-5 dark:border-ink-800">
        <h2 className="text-xl font-bold">{league.name}</h2>
        <p className="mt-1 text-ink-700 dark:text-ink-200">
          {league.competition} {league.seasonLabel}
        </p>
        <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
          {league.memberCount} di {league.maxParticipants} partecipanti
        </p>
      </div>

      {!valid && (
        <p role="alert" className="mt-4 font-medium text-danger">
          Questo invito non è più utilizzabile: potrebbe essere scaduto, esaurito, oppure la lega è
          al completo.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-4 font-medium text-danger">
          {error}
        </p>
      )}

      {valid && !user && (
        <div className="mt-6 flex flex-col gap-3">
          <p>Per entrare nella lega devi prima accedere o creare un account.</p>
          <Link
            to="/accedi"
            state={{ from: `/entra/${code}` }}
            className="button inline-flex items-center justify-center rounded-xl bg-panda-700 px-6 py-2.5 font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600"
          >
            Accedi
          </Link>
          <Link
            to="/registrati"
            className="button inline-flex items-center justify-center rounded-xl border-2 border-panda-700 px-6 py-2.5 font-semibold text-panda-700 no-underline hover:bg-panda-50 dark:border-panda-400 dark:text-panda-300"
          >
            Crea un account
          </Link>
        </div>
      )}

      {info.data.admissionRequirements && info.data.admissionRequirements.length > 0 && (
        <section aria-labelledby="join-requisiti" className="mt-4">
          <h3 id="join-requisiti" className="font-bold">Requisiti della lega</h3>
          <ul className="mt-2 list-disc pl-5 text-ink-700 dark:text-ink-200">
            {info.data.admissionRequirements.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}

      {valid && user && (
        <div className="mt-6">
          {info.data.quizRequired && (
            <p className="mb-3 text-ink-700 dark:text-ink-200">
              Questa lega prevede un <strong>quiz di ammissione</strong>: dopo l’iscrizione dovrai
              superarlo per diventare membro.
            </p>
          )}
          {requiresApproval && (
            <p className="mb-3 text-ink-700 dark:text-ink-200">
              L’amministratore della lega dovrà approvare la tua richiesta.
            </p>
          )}
          <Button
            onClick={async () => {
              setError(null);
              try {
                const res = await join.mutateAsync(code);
                announce(res.message);
                if (res.quizRequired) {
                  navigate(`/leghe/${res.leagueId}/quiz`);
                } else {
                  navigate(res.pending ? '/dashboard' : `/leghe/${res.leagueId}`);
                }
              } catch (err) {
                const message =
                  err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
                setError(message);
                announce(`Errore: ${message}`, 'assertive');
              }
            }}
            loading={join.isPending}
            loadingText="Ingresso in corso…"
          >
            Entra nella lega
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
